import { motion, AnimatePresence, MotionValue, PanInfo } from "framer-motion";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { RecipeDeckOnboardingOverlay } from "./RecipeDeckOnboardingOverlay";
import {
  normalizeIngredientLine,
  formatStructuredIngredient,
} from "../../shared/ingredientFormat";
import type { Recipe } from "../../types/recipe";
import type { V32UITier } from '../../types/subscription';
import {
  X,
  Heart,
  Star,
  Clock,
  ChefHat,
  Users,
  Timer,
  Zap,
  ArrowLeft,
  RefreshCw,
  HelpCircle,
} from "lucide-react";

interface RecipeSwiperViewProps {
  // Data
  recipes: Recipe[];
  currentRecipe: Recipe;
  currentIndex: number;
  selectedRecipes: Recipe[];
  savedRecipes: Recipe[];
  swipeDirection: string | null;
  showOverlay: boolean;
  tutorialCompleted: boolean;
  progress: number;
  refreshing: boolean;
  showProgress: boolean;
  currentTier: V32UITier;
  isFirstVisit: boolean;
  stackPreviewRecipes: Recipe[];
  overlayStyle: {
    background: string;
    icon: string;
    text: string;
    color: string;
  } | null;

  // Framer Motion values
  x: MotionValue<number>;
  y: MotionValue<number>;
  rotate: MotionValue<number>;
  opacity: MotionValue<number>;

  // Event handlers
  onBack: () => void;
  onRefresh?: () => void;
  onUpgrade?: () => void;
  onDragEnd: (event: any, info: PanInfo) => void;
  onButtonAction: (direction: "left" | "right" | "up" | "down") => void;
  onComplete: () => void;
  onTutorialComplete: (data: any) => void;
  onTutorialSkip: () => void;
  onReplayTutorial?: () => void;
}

/**
 * RecipeSwiper View - Pure UI component for recipe swiping interface
 *
 * This renders:
 * - Multi-directional swipe interface with gesture animations
 * - Recipe card stack visualization with previews
 * - Progress tracking and navigation controls
 * - Swipe overlay animations and feedback
 * - Action button alternatives for accessibility
 * - Tutorial onboarding overlay integration
 * - Tier-based upgrade prompts and completion states
 *
 * All business logic, gesture recognition, and state management are handled by the container.
 */
export default function RecipeSwiperView({
  recipes,
  currentRecipe,
  currentIndex,
  selectedRecipes,
  savedRecipes,
  swipeDirection,
  showOverlay,
  tutorialCompleted,
  progress,
  refreshing,
  showProgress,
  currentTier,
  isFirstVisit,
  stackPreviewRecipes,
  overlayStyle,
  x,
  y,
  rotate,
  opacity,
  onBack,
  onRefresh,
  onUpgrade,
  onDragEnd,
  onButtonAction,
  onComplete,
  onTutorialComplete,
  onTutorialSkip,
  onReplayTutorial,
}: RecipeSwiperViewProps) {
  const calorieValue =
    currentRecipe?.calories ?? currentRecipe?.nutritionInfo?.calories ?? null;
  const formattedCalories =
    calorieValue != null && !Number.isNaN(Number(calorieValue))
      ? `${Math.round(Number(calorieValue))}`
      : "—";
  // Completion state - no current recipe
  if (!currentRecipe) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-lynx p-4">
        <div className="w-full max-w-md space-y-4">
          {/* Upgrade Hint Based on Tier */}
          {onUpgrade && currentTier !== "core_plus" && (
            <Card className="to-spice/10 border-sage/30 from-sage/10 border bg-gradient-to-r">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sage mb-1 font-medium">
                      {currentTier === "demo"
                        ? "🌟 Loving the recipe discovery?"
                        : currentTier === "starter"
                          ? "🎯 Get more recipe recommendations"
                          : "🚀 Unlock advanced AI features"}
                    </p>
                    <p className="text-sm text-dusk">
                      {currentTier === "demo"
                        ? "Upgrade for unlimited meal plans and AI suggestions"
                        : currentTier === "starter"
                          ? "Family plan includes better AI personalization"
                          : "Master Chef gets unlimited AI recipe generations"}
                    </p>
                  </div>
                  <Button
                    onClick={onUpgrade}
                    size="sm"
                    className="bg-sage desktop-hover:bg-sage/90 text-white"
                  >
                    Upgrade
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Completion Card */}
          <Card className="overflow-hidden rounded-xl border-0 bg-white shadow-elevated">
            <CardContent className="space-y-6 p-8 text-center">
              <div className="text-5xl">🎉</div>
              <div className="space-y-2">
                <h2 className="text-2xl font-bold text-martinique">
                  Great choices!
                </h2>
                <p className="mx-auto max-w-md text-sm text-martinique/70">
                  You've liked {selectedRecipes.length} recipes and saved{" "}
                  {savedRecipes.length} for later. Let's create your
                  personalized meal plan!
                </p>
              </div>

              {/* Stats */}
              <div className="flex items-center justify-center gap-6 text-sm">
                <div className="flex flex-col items-center gap-1">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-coral-50">
                    <Heart className="h-5 w-5 text-coral" />
                  </div>
                  <span className="font-semibold text-martinique">
                    {selectedRecipes.length}
                  </span>
                  <span className="text-xs text-martinique/60">liked</span>
                </div>
                <div className="bg-stone/40 h-12 w-px"></div>
                <div className="flex flex-col items-center gap-1">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-sage-50">
                    <Star className="text-sage h-5 w-5" />
                  </div>
                  <span className="font-semibold text-martinique">
                    {savedRecipes.length}
                  </span>
                  <span className="text-xs text-martinique/60">saved</span>
                </div>
              </div>

              {/* Action Button */}
              <Button
                onClick={onComplete}
                className="w-full bg-coral text-white shadow-elevated transition-all desktop-hover:scale-105 desktop-hover:bg-coral-600 desktop-hover:shadow-prominent"
              >
                Create My Plan
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-lynx">
      {/* Mobile Header - Only visible on mobile */}
      <header className="border-stone/20 fixed left-0 right-0 top-0 z-50 flex h-14 items-center justify-between border-b bg-white/95 px-4 backdrop-blur-sm md:hidden">
        <button
          onClick={onBack}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-martinique/60 transition-colors desktop-hover:bg-lynx"
          aria-label="Go back"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-2">
          <div className="to-sage flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-coral shadow-sm">
            <span className="text-xs font-semibold text-white">N</span>
          </div>
          <span className="text-base font-semibold text-martinique">
            Discover
          </span>
        </div>

        <div className="flex items-center gap-2">
          {onReplayTutorial && (
            <button
              onClick={onReplayTutorial}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-martinique/60 transition-colors desktop-hover:bg-lynx"
              aria-label="Show tutorial"
              title="Learn how to use Recipe Discovery"
            >
              <HelpCircle className="h-5 w-5" />
            </button>
          )}
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={refreshing}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-martinique/60 transition-colors disabled:opacity-50 desktop-hover:bg-lynx"
              aria-label="Refresh recipes"
            >
              <RefreshCw
                className={`h-5 w-5 ${refreshing ? "animate-spin" : ""}`}
              />
            </button>
          )}
        </div>
      </header>

      {/* Main Scrollable Container */}
      <div className="flex-1 overflow-y-auto pt-14 md:pt-0">
        {/* Swiper Container - Full Height */}
        <div className="relative flex min-h-[calc(100vh-3.5rem)] items-center justify-center p-4 md:min-h-screen md:p-4">
          <div className="relative h-[600px] w-full max-w-sm">
            {/* Stack Preview - Show next cards behind */}
            {stackPreviewRecipes.map((recipe, index) => (
              <div
                key={recipe.id}
                className="pointer-events-none absolute inset-0"
                style={{
                  transform: `translateY(${(index + 1) * 8}px) scale(${1 - (index + 1) * 0.05})`,
                  zIndex: 10 - (index + 1),
                }}
              >
                <Card className="h-full overflow-hidden rounded-xl border-0 bg-stone shadow-subtle">
                  <CardContent className="h-full p-4">
                    <div className="text-center text-sm text-martinique/60">
                      {recipe.title}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}

            {/* Current Card */}
            <motion.div
              className="absolute inset-0 cursor-grab active:cursor-grabbing"
              style={{ x, y, rotate, opacity, zIndex: 20 }}
              drag
              dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
              dragElastic={0.7}
              onDragEnd={onDragEnd}
              whileDrag={{ scale: 1.05 }}
            >
              <Card className="h-full overflow-hidden rounded-xl border-0 bg-white shadow-elevated">
                {/* Swipe Overlay */}
                {showOverlay && overlayStyle && (
                  <motion.div
                    className="absolute inset-0 z-30 flex items-center justify-center"
                    style={{ background: overlayStyle.background }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <div
                      className="text-center"
                      style={{ color: overlayStyle.color }}
                    >
                      <div className="mb-2 text-6xl">{overlayStyle.icon}</div>
                      <div className="text-2xl font-bold">
                        {overlayStyle.text}
                      </div>
                    </div>
                  </motion.div>
                )}

                <CardContent className="flex h-full flex-col p-0">
                  {/* Recipe Image */}
                  <div className="relative h-48 overflow-hidden bg-stone">
                    <ImageWithFallback
                      src={
                        currentRecipe?.heroImageUrl ??
                        currentRecipe?.imageUrl ??
                        ""
                      }
                      alt={currentRecipe?.title ?? "Recipe image"}
                      className="h-full w-full object-cover"
                    />
                    <div className="absolute right-4 top-4">
                      <Badge className="bg-white/90 text-ink">
                        <Timer className="mr-1 h-3 w-3" />
                        {currentRecipe?.totalMinutes ?? "—"}min
                      </Badge>
                    </div>
                  </div>

                  {/* Recipe Info */}
                  <div className="flex-1 space-y-4 p-6">
                    <div>
                      <h3 className="mb-2 text-2xl font-bold text-dusk">
                        {currentRecipe?.title ?? "—"}
                      </h3>
                      <p className="text-supporting line-clamp-3 text-sm">
                        {currentRecipe?.description ||
                          "No description available"}
                      </p>
                    </div>

                    {/* Quick Stats */}
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1">
                        <Zap className="h-4 w-4 text-coral" />
                        <span className="text-martinique">
                          {formattedCalories} cal
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <ChefHat className="text-sage h-4 w-4" />
                        <span className="capitalize text-martinique">
                          {currentRecipe?.difficulty ?? "—"}
                        </span>
                      </div>
                    </div>

                    {/* Cuisine/Region - More Prominent */}
                    {currentRecipe?.cuisine && (
                      <div className="rounded-lg bg-coral-50 px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">🌍</span>
                          <div>
                            <p className="text-xs font-medium text-coral-800">
                              Cuisine
                            </p>
                            <p className="text-sm font-semibold text-coral-900">
                              {currentRecipe.cuisine}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2">
                      {Array.isArray(currentRecipe?.tags)
                        ? currentRecipe.tags.slice(0, 3).map((tag) => (
                            <Badge
                              key={tag}
                              variant="outline"
                              className="border-sage-200 bg-sage-50 text-xs text-sage-800"
                            >
                              {tag}
                            </Badge>
                          ))
                        : Array.isArray(currentRecipe?.dietaryTags)
                          ? currentRecipe.dietaryTags.slice(0, 3).map((tag) => (
                              <Badge
                                key={tag}
                                variant="outline"
                                className="border-sage-200 bg-sage-50 text-xs text-sage-800"
                              >
                                {tag}
                              </Badge>
                            ))
                          : null}
                    </div>

                    {/* Key Ingredients - Enhanced Display */}
                    <div className="border-stone/20 bg-cream rounded-lg border p-3">
                      <h4 className="mb-2 flex items-center gap-2 font-semibold text-martinique">
                        <ChefHat className="h-4 w-4 text-coral" />
                        Key ingredients
                      </h4>
                      <ul className="space-y-1.5 text-sm">
                        {Array.isArray(currentRecipe?.ingredients) ? (
                          currentRecipe.ingredients
                            .slice(0, 5)
                            .map((ingredient, index) => (
                              <li
                                key={index}
                                className="flex items-start gap-2"
                              >
                                <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-coral"></div>
                                <span className="text-martinique/80">
                                  {typeof ingredient === "string"
                                    ? normalizeIngredientLine(ingredient)
                                    : formatStructuredIngredient({
                                        amount: ingredient.displayQty
                                          ? String(ingredient.displayQty)
                                          : ingredient.quantity
                                            ? String(ingredient.quantity)
                                            : undefined,
                                        unit: ingredient.displayUnit,
                                        name:
                                          ingredient.notes ||
                                          ingredient.ingredientId ||
                                          "",
                                        note: ingredient.notes,
                                        raw: ingredient.quantity
                                          ? String(ingredient.quantity)
                                          : undefined,
                                      })}
                                </span>
                              </li>
                            ))
                        ) : Array.isArray(currentRecipe?.instructions) ? (
                          currentRecipe.instructions
                            .slice(0, 5)
                            .map((step, index) => (
                              <li
                                key={index}
                                className="flex items-start gap-2"
                              >
                                <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-coral"></div>
                                <span className="line-clamp-2 text-martinique/80">
                                  {step}
                                </span>
                              </li>
                            ))
                        ) : (
                          <li className="text-xs text-martinique/50">
                            No ingredients available
                          </li>
                        )}
                        {Array.isArray(currentRecipe?.ingredients) &&
                          currentRecipe.ingredients.length > 5 && (
                            <li className="pt-1 text-xs font-medium text-coral">
                              +{currentRecipe.ingredients.length - 5} more
                              ingredients
                            </li>
                          )}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Swipe Hints */}
          <div className="pointer-events-none absolute bottom-8 left-1/2 -translate-x-1/2 transform">
            <div className="flex items-center gap-2 rounded-full bg-martinique/80 px-4 py-2 text-xs text-white shadow-lg backdrop-blur-sm">
              <div className="h-2 w-2 animate-pulse rounded-full bg-white"></div>
              <span className="font-medium">Swipe to decide</span>
            </div>
          </div>
        </div>

        {/* Action Buttons (Scroll to reveal) */}
        <div className="safe-area-inset-bottom border-stone/20 border-t bg-white">
          <div className="mx-auto max-w-sm px-4 py-6">
            {/* Section Header */}
            <div className="mb-4 text-center">
              <p className="text-sm font-medium text-martinique">
                Manual Controls
              </p>
              <p className="mt-1 text-xs text-martinique/60">
                Tap instead of swiping
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-center gap-4">
              <Button
                variant="outline"
                size="lg"
                onClick={() => onButtonAction("left")}
                className="h-14 w-14 rounded-full border-2 border-martinique/20 text-martinique/60 transition-all desktop-hover:border-martinique/40 desktop-hover:bg-martinique/5"
                aria-label="Skip recipe"
              >
                <X className="h-6 w-6" />
              </Button>

              <Button
                variant="outline"
                size="lg"
                onClick={() => onButtonAction("down")}
                className="border-sage/40 text-sage desktop-hover:border-sage desktop-hover:bg-sage/10 h-12 w-12 rounded-full border-2 transition-all"
                aria-label="Save for later"
              >
                <Clock className="h-5 w-5" />
              </Button>

              <Button
                variant="outline"
                size="lg"
                onClick={() => onButtonAction("up")}
                className="h-12 w-12 rounded-full border-2 border-coral/40 text-coral transition-all desktop-hover:border-coral desktop-hover:bg-coral/10"
                aria-label="Favorite recipe"
              >
                <Star className="h-5 w-5" />
              </Button>

              <Button
                variant="default"
                size="lg"
                onClick={() => onButtonAction("right")}
                className="h-14 w-14 rounded-full bg-coral text-white shadow-elevated transition-all desktop-hover:scale-105 desktop-hover:bg-coral-600 desktop-hover:shadow-prominent"
                aria-label="Add to plan"
              >
                <Heart className="h-6 w-6" />
              </Button>
            </div>

            {/* Action Labels */}
            <div className="mx-auto mt-3 flex max-w-sm items-center justify-center gap-4 text-xs text-martinique/60">
              <span className="w-14 text-center">Skip</span>
              <span className="w-12 text-center">Maybe</span>
              <span className="w-12 text-center">Save</span>
              <span className="w-14 text-center">Cook</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tutorial Overlay */}
      {isFirstVisit && !tutorialCompleted && (
        <RecipeDeckOnboardingOverlay
          isFirstVisit={isFirstVisit}
          canSkip={true}
          recipeCards={recipes}
          onComplete={onTutorialComplete}
          onSkip={onTutorialSkip}
        />
      )}
    </div>
  );
}
